import {
  useContext,
  createContext,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import { useFragment, useSuspenseQuery, useQuery } from '@apollo/client/react';
import { GQL_FRAGMENT_USER, GQL_QUERY_ME } from '../utility/queries';
import { CompanyUserRole, UserFragmentFragment } from '@sc/graphql';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useAlerts } from './alerts';
import { identifyUser } from '../utility/analytics';

export interface GenericModalProps {
  mounted: boolean;
  onConfirm?: () => void;
  confirmText?: string;
  onDecline?: () => void;
  declineText?: string;
  title?: string;
  content: ReactNode;
  type: 'error' | 'info' | 'success' | 'warning';
}

export interface UserContext {
  user: UserFragmentFragment;
  isAdmin: boolean;
  logout: () => void;
}

const UserContext = createContext<UserContext>({} as UserContext);

export function UserProvider({ children }: { children: ReactNode }) {
  const { data: userRsp } = useSuspenseQuery(GQL_QUERY_ME);

  const doLogout = useCallback(() => {
    sessionStorage.clear();

    localStorage.clear();
  }, []);

  const user = useFragment({
    fragment: GQL_FRAGMENT_USER,
    from: userRsp.user,
  });

  if (!user.complete) throw new Error('Invalid user');

  const userEmail = user.data.email;

  const userName = user.data.name;

  useEffect(() => {
    identifyUser({ email: userEmail, name: userName });
  }, [userEmail, userName]);

  const search = useSearch({
    from: '/_authenticated/billing',
    shouldThrow: false,
  });

  const navigate = useNavigate();

  const { startPolling, stopPolling } = useQuery(GQL_QUERY_ME, {
    skip: !search?.stripeState,
    fetchPolicy: 'network-only',
  });

  const { setAlertModal } = useAlerts();

  useEffect(() => {
    if (search?.stripeState) {
      if (search.stripeState === 'success') {
        startPolling(250);
      } else {
        setAlertModal({
          open: true,
          title: 'Unable to process charge',
          description:
            'Something went wrong processing your upgrade request, please try again.',
        });
      }
    }
  }, [search?.stripeState, setAlertModal, startPolling]);

  useEffect(() => {
    if (search?.stripeState && user.data.company.subscription) {
      stopPolling();

      void navigate({
        to: '.',
        search: (prev) => ({ ...prev, stripeState: undefined }),
      });
    }
  }, [
    user.data.company.subscription,
    stopPolling,
    navigate,
    search?.stripeState,
  ]);

  return (
    <UserContext.Provider
      value={{
        isAdmin:
          user.data.role === CompanyUserRole.admin ||
          user.data.role === CompanyUserRole.accountOwner,
        user: user.data,
        logout: doLogout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);

  return context;
}
